import React, { forwardRef, useRef, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Text } from "react_reusable_components";
import variables from "../../../../../themes/base/styles/variables";
import useResponsive from "../../../../../core/hooks/useResponsive";
import { Space } from "antd";
import html2canvas from "html2canvas";
import { getConditionalContent } from "../../../../../UtilityFunctions/utils";

export const LevelGraphs = {
  toolTipStyle: {
    width: variables.fullWidth,
    height: variables.fullHeight,
    backgroundColor: variables.bodyBg,
    padding: variables.customizedTooltipPadding,
    border: variables.customizedTooltipBorder,
  },
  customLegendParentStyles: {
    display: variables.dFlex,
    justifyContent: variables.justifyCenter,
    columnGap: variables.gapSmall,
  },
  customLegendChildStyles: {
    display: variables.dFlex,
    justifyContent: variables.spaceBetween,
  },
  labelBox2StylesMediumSize: {
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    gap: variables.gapXS2,
  },
  customLegendStyle: (color) => ({
    width: variables.box,
    height: variables.box,
    backgroundColor: color, //taking color from props
  }),
  customLegendMobileSize: {
    flexDirection: variables.flexDirection,
    alignItems: variables.dFlex2,
    gap: variables.gapXS1,
    marginLeft: variables.mediumHeight,
  },
  labelBox: {
    fontSize: variables.fontSizeNormal,
    textTransform: "capitalize",
  },
  labelBoxMobileSize: {
    fontSize: variables.fontSizeNormal,
    textTransform: "capitalize",
  },
  labelBox2StylesMobileSize: {
    display: variables.dFlex,
    alignItems: variables.alignCenter,
    gap: variables.gapXS1,
  },
};

const Graph = forwardRef(
  (
    {
      data,
      width,
      height,
      barSize,
      dataKey1,
      axisLine,
      color1,
      color2,
      dataKey2,
      linkLabel,
      legend1,
      legend2,
      dataKey3,
      dataKey4,
      isPercentageChecked,
    },
    ref
  ) => {
    const chartRef = useRef(null);

    const downloadChartImage = useCallback(() => {
      const node = chartRef.current;
      // Call the downloadChartImage function of the Graph component only if it is mounted
      try {
        if (node) {
          html2canvas(node).then((canvas) => {
            const link = document.createElement("a");
            link.download = linkLabel;
            link.href = canvas.toDataURL();
            link.click();
          });
        }
      } catch (error) {} //NOSONAR
    }, [chartRef]);

    ref.current = {
      downloadChartImage,
    };

    const CustomTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        return (
          <div style={LevelGraphs.toolTipStyle}>
            {!isPercentageChecked && (
              <p
                style={{ color: color2 }}
              >{`${legend2} : ${payload[0].payload.questionCount}`}</p>
            )}
            <p style={{ color: color1 }}>{`${legend1} : ${
              payload[0].payload.okCount
            }${getConditionalContent({
              condition: isPercentageChecked,
              trueValue: "%",
              falseValue: "",
            })}`}</p>
          </div>
        );
      }

      return null;
    };
    const legends = [
      {
        key: dataKey2,
        color: color1,
      },
      !isPercentageChecked && {
        key: dataKey4,
        color: color2,
      },
    ].filter(Boolean);
    const CustomLegend = () => {
      const response = useResponsive();
      return (
        <Space
          style={{
            ...getConditionalContent({
              condition: response.isMd,
              trueValue: LevelGraphs.customLegendParentStyles,
              falseValue: LevelGraphs.customLegendMobileSize,
            }),
            marginTop: variables.marginMedium,
          }}
        >
          {legends?.map(({ key, color }, index) => (
            <>
              <div
                style={getConditionalContent({
                  condition: response.isMd,
                  trueValue: LevelGraphs.labelBox2StylesMediumSize,
                  falseValue: LevelGraphs.labelBox2StylesMobileSize,
                })}
              >
                <div
                  key={`item-${index}`}
                  style={LevelGraphs.customLegendStyle(color)}
                />
                <Text
                  style={getConditionalContent({
                    condition: response.isMd,
                    trueValue: LevelGraphs.labelBox,
                    falseValue: LevelGraphs.labelBoxMobileSize,
                  })}
                >
                  {key}
                </Text>
              </div>
            </>
          ))}
        </Space>
      );
    };

    return (
      <div ref={chartRef} style={{ width: width }}>
        <ResponsiveContainer width={width} height={height}>
          <BarChart data={data} barSize={barSize} margin={{ left: -20 }}>
            <XAxis dataKey={dataKey3} axisLine={axisLine} />
            <YAxis axisLine={axisLine} />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "transparent" }}
            />
            <Legend content={CustomLegend} />
            <Bar dataKey={dataKey2} fill={color1} name={legend1} stackId="1" />
            <Bar
              dataKey={dataKey1}
              fill={getConditionalContent({
                condition: isPercentageChecked,
                trueValue: "transparent",
                falseValue: color2,
              })}
              name={legend2}
              stackId="1"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
);

export default Graph;
